import { useState, useEffect } from 'react';
import { eodhd } from '../services/eodhd';
import { tradingApi } from '../services/tradingApi';
import type { NewsArticle, EconomicEvent } from '../types';
import type { ForexNewsEvent } from '../types/trading';
import { ExternalLink, Calendar, TrendingUp, TrendingDown, Minus, AlertTriangle, Clock, Brain, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import type { MarketOutlook } from '../types/trading';
import EventDetailOverlay from '../components/EventDetailOverlay';
import { usePermission } from '../hooks/usePermission';
import { UpgradeNudge } from '../components/UpgradeNudge';

type Tab = 'news' | 'forex';

interface MetalPairConfig {
  symbol: string;
  name: string;
  currencies: string[];
}

const METALS_PAIRS: MetalPairConfig[] = [
  { symbol: 'XAUUSD', name: 'Gold / US Dollar',   currencies: ['USD'] },
  { symbol: 'XAGUSD', name: 'Silver / US Dollar', currencies: ['USD'] },
];

const COUNTRY_FLAGS: Record<string, string> = {
  USD: '\u{1F1FA}\u{1F1F8}',
  EUR: '\u{1F1EA}\u{1F1FA}',
  GBP: '\u{1F1EC}\u{1F1E7}',
  JPY: '\u{1F1EF}\u{1F1F5}',
  AUD: '\u{1F1E6}\u{1F1FA}',
  NZD: '\u{1F1F3}\u{1F1FF}',
  CAD: '\u{1F1E8}\u{1F1E6}',
  CHF: '\u{1F1E8}\u{1F1ED}',
  CNY: '\u{1F1E8}\u{1F1F3}',
  ZAR: '\u{1F1FF}\u{1F1E6}',
  ALL: '\u{1F30D}',
};

export default function NewsCalendar() {
  const { allowed: canViewForexCalendar, loading: permLoading } = usePermission('feature:forex-calendar');
  const [activeTab, setActiveTab] = useState<Tab>('forex');
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [events, setEvents] = useState<EconomicEvent[]>([]);
  const [forexEvents, setForexEvents] = useState<ForexNewsEvent[]>([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingForex, setLoadingForex] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [forexCountryFilter, setForexCountryFilter] = useState<string>('');
  const [forexImpactFilter, setForexImpactFilter] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<ForexNewsEvent | null>(null);
  const [newsLimit] = useState(20);
  const [marketOutlook, setMarketOutlook] = useState<MarketOutlook | null>(null);
  const [loadingOutlook, setLoadingOutlook] = useState(true);
  const [outlookExpanded, setOutlookExpanded] = useState(false);

  useEffect(() => {
    loadMarketOutlook();
  }, []);

  useEffect(() => {
    if (activeTab === 'news') {
      loadNews();
      loadEvents();
    } else {
      loadForexCalendar();
    }
  }, [selectedCountry, activeTab]);

  const loadMarketOutlook = async () => {
    try {
      setLoadingOutlook(true);
      const data = await tradingApi.getMarketOutlook();
      if (data && !('detail' in data)) {
        setMarketOutlook(data);
      }
    } catch {
      // Pro+ required or not yet generated — silent fail
    } finally {
      setLoadingOutlook(false);
    }
  };

  const loadNews = async () => {
    try {
      setLoadingNews(true);
      const response = await eodhd.getNews(undefined, newsLimit);
      setNews(response.articles);
    } catch (err) {
      console.error('Failed to load news:', err);
    } finally {
      setLoadingNews(false);
    }
  };

  const loadEvents = async () => {
    try {
      setLoadingEvents(true);
      const today = new Date();
      const nextWeek = new Date();
      nextWeek.setDate(today.getDate() + 7);

      const response = await eodhd.getEconomicEvents(
        today.toISOString().split('T')[0],
        nextWeek.toISOString().split('T')[0],
        selectedCountry || undefined
      );
      setEvents(response.events);
    } catch (err) {
      console.error('Failed to load economic events:', err);
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadForexCalendar = async () => {
    try {
      setLoadingForex(true);
      const data = await tradingApi.getForexCalendar();
      setForexEvents(data);
    } catch (err) {
      console.error('Failed to load forex calendar:', err);
    } finally {
      setLoadingForex(false);
    }
  };

  const getSentimentIcon = (sentiment?: string) => {
    if (!sentiment) return null;
    const lower = sentiment.toLowerCase();
    if (lower.includes('positive') || lower.includes('bullish')) {
      return <TrendingUp className="w-4 h-4 text-green-400" />;
    } else if (lower.includes('negative') || lower.includes('bearish')) {
      return <TrendingDown className="w-4 h-4 text-red-400" />;
    }
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getSentimentColor = (sentiment?: string) => {
    if (!sentiment) return 'text-gray-400';
    const lower = sentiment.toLowerCase();
    if (lower.includes('positive') || lower.includes('bullish')) return 'text-green-400';
    if (lower.includes('negative') || lower.includes('bearish')) return 'text-red-400';
    return 'text-gray-400';
  };

  const getImpactColor = (impact?: string) => {
    if (!impact) return 'text-gray-400';
    const lower = impact.toLowerCase();
    if (lower.includes('high')) return 'text-red-400';
    if (lower.includes('medium')) return 'text-yellow-400';
    if (lower.includes('low')) return 'text-green-400';
    return 'text-gray-400';
  };

  const getImpactBg = (impact?: string) => {
    if (!impact) return 'bg-gray-700';
    const lower = impact.toLowerCase();
    if (lower.includes('high')) return 'bg-red-500/20';
    if (lower.includes('medium')) return 'bg-amber-500/20';
    if (lower.includes('low')) return 'bg-slate-700';
    return 'bg-gray-700';
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);

      if (diffHours < 1) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return `${diffMins}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch {
      return dateStr;
    }
  };

  const formatEventDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const isWithinHours = (dateStr: string, hours: number) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      return diffMs > 0 && diffMs < hours * 3600000;
    } catch {
      return false;
    }
  };

  const filteredForexEvents = forexEvents.filter((e) => {
    if (forexCountryFilter && e.country !== forexCountryFilter) return false;
    if (forexImpactFilter && e.impact !== forexImpactFilter) return false;
    return true;
  });

  // Group forex events by day
  const groupedForexEvents: Record<string, ForexNewsEvent[]> = {};
  for (const event of filteredForexEvents) {
    const day = new Date(event.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    if (!groupedForexEvents[day]) groupedForexEvents[day] = [];
    groupedForexEvents[day].push(event);
  }

  const uniqueCountries = [...new Set(forexEvents.map((e) => e.country))].sort();

  // Find latest prediction for a given symbol across all forex events
  const getLatestPrediction = (symbol: string) => {
    let prediction: { bias: string; confidence: number; reasoning?: string | null } | null = null;
    let predictionEventTime: Date | null = null;
    for (const event of forexEvents) {
      const p = (event.predictions ?? []).find((pr) => pr.symbol === symbol);
      if (p) {
        const t = new Date(event.date);
        if (!predictionEventTime || t > predictionEventTime) {
          prediction = p;
          predictionEventTime = t;
        }
      }
    }
    return prediction;
  };

  return (
    <>
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Market News & Economic Calendar</h1>
        <p className="text-gray-400">Stay informed with the latest market news and upcoming economic events</p>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 mb-6 bg-gray-800 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('forex')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'forex'
              ? 'bg-purple-600 text-white'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Forex Calendar
          </span>
        </button>
        <button
          onClick={() => setActiveTab('news')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'news'
              ? 'bg-purple-600 text-white'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            EODHD News
          </span>
        </button>
      </div>

      {activeTab === 'forex' ? (
        /* ═══ Forex Calendar Tab ═══ */
        permLoading ? null : !canViewForexCalendar ? (
          <UpgradeNudge
            tier="pro"
            features={[
              'Weekly forex economic calendar with impact ratings',
              'AI directional bias per pair before high-impact events',
              'Gold & Silver AI outlook with confidence scores',
              'Event detail overlay with full AI reasoning',
            ]}
          />
        ) : (
        <div>
          {/* Market Outlook */}
          {!loadingOutlook && marketOutlook && (
            <div className="mb-5">
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-400" />
                Market Outlook
                <span className="text-xs font-normal text-gray-500 ml-auto">
                  {new Date(marketOutlook.generated_at).toLocaleString()}
                </span>
              </h2>
              <div className="bg-gray-800/80 border border-gray-700 rounded-lg p-4 space-y-3">
                {/* Headline + sentiment */}
                <div className="flex items-start gap-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase whitespace-nowrap ${
                    marketOutlook.risk_sentiment === 'risk-on' ? 'bg-green-500/20 text-green-400' :
                    marketOutlook.risk_sentiment === 'risk-off' ? 'bg-red-500/20 text-red-400' :
                    marketOutlook.risk_sentiment === 'mixed' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-gray-600 text-gray-300'
                  }`}>
                    {marketOutlook.risk_sentiment}
                  </span>
                  <p className="text-white text-sm font-medium">{marketOutlook.headline}</p>
                </div>

                {/* Regional outlook chips */}
                {marketOutlook.regional_outlook && Object.keys(marketOutlook.regional_outlook).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(marketOutlook.regional_outlook).map(([region, outlook]) => (
                      <span key={region} className={`px-2 py-0.5 rounded text-xs ${
                        outlook === 'bullish' ? 'bg-green-500/10 text-green-400' :
                        outlook === 'bearish' ? 'bg-red-500/10 text-red-400' :
                        'bg-gray-700 text-gray-400'
                      }`}>
                        {region}: {outlook}
                      </span>
                    ))}
                  </div>
                )}

                {/* Key events */}
                {marketOutlook.key_events?.length > 0 && (
                  <div className="space-y-1">
                    {marketOutlook.key_events.slice(0, outlookExpanded ? undefined : 3).map((evt, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span className={`mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          evt.impact === 'high' ? 'bg-red-400' :
                          evt.impact === 'medium' ? 'bg-yellow-400' : 'bg-gray-500'
                        }`} />
                        <span className="text-gray-300">
                          <span className="text-white font-medium">{evt.title}</span>
                          {evt.affected_assets?.length > 0 && (
                            <span className="text-gray-500 ml-1">({evt.affected_assets.join(', ')})</span>
                          )}
                          {evt.summary && <span className="text-gray-400"> — {evt.summary}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Expand/collapse full narrative */}
                <button
                  onClick={() => setOutlookExpanded(!outlookExpanded)}
                  className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                >
                  {outlookExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {outlookExpanded ? 'Show less' : 'Full briefing'}
                </button>

                {outlookExpanded && (
                  <div className="text-sm text-gray-300 leading-relaxed border-t border-gray-700 pt-3 whitespace-pre-line">
                    {marketOutlook.outlook_md}
                  </div>
                )}

                {/* Sources */}
                {outlookExpanded && marketOutlook.sources?.length > 0 && (
                  <div className="border-t border-gray-700 pt-2">
                    <p className="text-xs text-gray-500 mb-1">Sources:</p>
                    <div className="flex flex-wrap gap-2">
                      {marketOutlook.sources.map((s, i) => (
                        <a
                          key={i}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400/70 hover:text-blue-300 flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {s.title.slice(0, 40)}{s.title.length > 40 ? '...' : ''}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Gold & Silver AI Outlook */}
          <div className="mb-5">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Brain className="w-4 h-4 text-purple-400" />
              Precious Metals AI Outlook
            </h2>
            {loadingForex ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[0, 1].map((i) => (
                  <div key={i} className="bg-gray-800 rounded-lg p-3 space-y-2">
                    <div className="h-3 bg-gray-700 rounded animate-pulse w-1/2" />
                    <div className="h-6 bg-gray-700 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {METALS_PAIRS.map((metal) => {
                  const prediction = getLatestPrediction(metal.symbol);
                  const biasColor = prediction
                    ? prediction.bias === 'BUY'
                      ? 'text-green-400'
                      : prediction.bias === 'SELL'
                      ? 'text-red-400'
                      : 'text-gray-400'
                    : 'text-gray-500';
                  const biasBg = prediction
                    ? prediction.bias === 'BUY'
                      ? 'bg-green-500/20 border-green-500/30'
                      : prediction.bias === 'SELL'
                      ? 'bg-red-500/20 border-red-500/30'
                      : 'bg-gray-600/20 border-gray-600/30'
                    : 'bg-gray-700/30 border-gray-700/30';
                  const upcomingEvents = forexEvents.filter(
                    (e) =>
                      metal.currencies.includes(e.country) &&
                      e.impact === 'High' &&
                      isWithinHours(e.date, 8)
                  );
                  return (
                    <div key={metal.symbol} className={`rounded-lg border p-3 ${biasBg} flex items-start gap-3`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-gray-100 text-sm">{metal.symbol}</span>
                          <span className="text-xs text-gray-500">{metal.name}</span>
                        </div>
                        {prediction ? (
                          <div className="flex items-center gap-2">
                            <Brain className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                            <span className={`font-bold ${biasColor}`}>{prediction.bias}</span>
                            <span className="text-gray-400 text-xs">{prediction.confidence}% confidence</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-gray-500 text-xs">
                            <Brain className="w-3.5 h-3.5" />
                            <span>No prediction yet</span>
                          </div>
                        )}
                        {prediction?.reasoning && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{prediction.reasoning}</p>
                        )}
                      </div>
                      {upcomingEvents.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-amber-400 flex-shrink-0">
                          <Clock className="w-3 h-3" />
                          <span>{upcomingEvents.length} upcoming</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <select
              value={forexCountryFilter}
              onChange={(e) => setForexCountryFilter(e.target.value)}
              className="text-sm bg-gray-700 border border-gray-600 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Countries</option>
              {uniqueCountries.map((c) => (
                <option key={c} value={c}>{COUNTRY_FLAGS[c] || ''} {c}</option>
              ))}
            </select>
            <select
              value={forexImpactFilter}
              onChange={(e) => setForexImpactFilter(e.target.value)}
              className="text-sm bg-gray-700 border border-gray-600 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Impact</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
              <option value="Holiday">Holiday</option>
            </select>
            <button
              onClick={loadForexCalendar}
              className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
            >
              Refresh
            </button>
            <span className="text-xs text-gray-500 ml-auto">
              {filteredForexEvents.length} events this week
            </span>
          </div>

          {loadingForex ? (
            <div className="space-y-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-lg p-4 space-y-2">
                  <div className="h-4 bg-gray-700 rounded animate-pulse w-3/4" />
                  <div className="h-3 bg-gray-700 rounded animate-pulse w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredForexEvents.length === 0 ? (
            <div className="bg-gray-800 rounded-lg p-8 text-center">
              <p className="text-gray-400">No forex events found for this week</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedForexEvents).map(([day, dayEvents]) => (
                <div key={day}>
                  <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">{day}</h3>
                  <div className="bg-gray-800 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[700px]">
                      <thead>
                        <tr className="border-b border-gray-700 text-xs text-gray-500 uppercase">
                          <th className="text-left px-4 py-2">Time</th>
                          <th className="text-left px-4 py-2">Country</th>
                          <th className="text-left px-4 py-2">Event</th>
                          <th className="text-center px-4 py-2">Impact</th>
                          <th className="text-right px-4 py-2">Forecast</th>
                          <th className="text-right px-4 py-2">Previous</th>
                          <th className="text-right px-4 py-2">Actual</th>
                          <th className="text-left px-4 py-2">AI Prediction</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayEvents.map((event, idx) => {
                          const upcoming = isWithinHours(event.date, 4);
                          return (
                            <tr
                              key={idx}
                              onClick={() => setSelectedEvent(event)}
                              className={`border-b border-gray-700/50 hover:bg-purple-500/5 cursor-pointer transition-colors ${
                                upcoming ? 'bg-amber-500/5' : ''
                              }`}
                            >
                              <td className="px-4 py-2.5 whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                  {upcoming && <Clock className="w-3 h-3 text-amber-400" />}
                                  <span className={upcoming ? 'text-amber-300 font-medium' : 'text-gray-300'}>
                                    {new Date(event.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-2.5 whitespace-nowrap">
                                <span title={event.country}>{COUNTRY_FLAGS[event.country] || event.country} {event.country}</span>
                              </td>
                              <td className="px-4 py-2.5">
                                <span className="text-gray-100">{event.title}</span>
                              </td>
                              <td className="px-4 py-2.5 text-center">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getImpactColor(event.impact)} ${getImpactBg(event.impact)}`}>
                                  {event.impact}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-right text-gray-300 font-mono text-xs">
                                {event.forecast || '-'}
                              </td>
                              <td className="px-4 py-2.5 text-right text-gray-400 font-mono text-xs">
                                {event.previous || '-'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-xs">
                                {event.actual ? (
                                  <span className="text-green-400 font-medium">{event.actual}</span>
                                ) : (
                                  <span className="text-gray-600">-</span>
                                )}
                              </td>
                              <td className="px-4 py-2.5">
                                {event.predictions && event.predictions.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {event.predictions.map((pred, pIdx) => (
                                      <span
                                        key={pIdx}
                                        title={pred.reasoning || `${pred.symbol}: ${pred.bias} (${pred.confidence}%)`}
                                        className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full font-medium ${
                                          pred.bias === 'BUY'
                                            ? 'bg-green-500/20 text-green-400'
                                            : pred.bias === 'SELL'
                                            ? 'bg-red-500/20 text-red-400'
                                            : 'bg-gray-600/30 text-gray-400'
                                        }`}
                                      >
                                        <Brain className="w-3 h-3" />
                                        {pred.symbol} {pred.bias} {pred.confidence}%
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-gray-600 text-xs">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )
      ) : (
        /* ═══ EODHD News Tab ═══ */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Latest News (2/3 width) */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Latest Market News</h2>
                <button
                  onClick={loadNews}
                  className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Refresh
                </button>
              </div>

              {loadingNews ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="border border-gray-700 rounded-lg p-4 space-y-2">
                      <div className="h-4 bg-gray-700 rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-gray-700 rounded animate-pulse w-1/2" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {news.map((article, index) => (
                    <a
                      key={index}
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group hover:bg-gray-750 rounded-lg p-4 transition-colors border border-gray-700 hover:border-gray-600"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-100 group-hover:text-purple-400 transition-colors line-clamp-2 mb-2">
                            {article.title}
                          </h3>

                          {article.content && (
                            <p className="text-sm text-gray-400 line-clamp-2 mb-2">
                              {article.content}
                            </p>
                          )}

                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>{formatDate(article.date)}</span>
                            </div>

                            {article.sentiment && (
                              <div className={`flex items-center gap-1 ${getSentimentColor(article.sentiment)}`}>
                                {getSentimentIcon(article.sentiment)}
                                <span className="capitalize">{article.sentiment}</span>
                              </div>
                            )}

                            {article.symbols && article.symbols.length > 0 && (
                              <div className="flex items-center gap-1">
                                <span className="text-gray-600">•</span>
                                <span className="truncate max-w-[200px]">
                                  {article.symbols.slice(0, 3).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-purple-400 transition-colors flex-shrink-0" />
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Economic Calendar (1/3 width) */}
          <div>
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Economic Calendar</h2>
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="text-sm bg-gray-700 border border-gray-600 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All Countries</option>
                  <option value="US">United States</option>
                  <option value="GB">United Kingdom</option>
                  <option value="ZA">South Africa</option>
                  <option value="EU">European Union</option>
                  <option value="CN">China</option>
                  <option value="JP">Japan</option>
                </select>
              </div>

              {loadingEvents ? (
                <div className="space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="border border-gray-700 rounded-lg p-3 space-y-2">
                      <div className="h-3 bg-gray-700 rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-gray-700 rounded animate-pulse w-1/2" />
                    </div>
                  ))}
                </div>
              ) : events.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">
                  No upcoming economic events found
                </p>
              ) : (
                <div className="space-y-3">
                  {events.slice(0, 20).map((event, index) => (
                    <div
                      key={index}
                      className="border border-gray-700 rounded-lg p-3 hover:border-gray-600 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="font-medium text-sm text-gray-100 line-clamp-2">
                          {event.event}
                        </h4>
                        {event.impact && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${getImpactColor(event.impact)} bg-gray-700`}>
                            {event.impact}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="font-medium text-gray-400">{event.country}</span>
                        {event.currency && (
                          <>
                            <span className="text-gray-600">•</span>
                            <span>{event.currency}</span>
                          </>
                        )}
                      </div>

                      <div className="text-xs text-gray-500 mt-1">
                        {formatEventDate(event.date)}
                      </div>

                      {(event.actual || event.estimate || event.previous) && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-xs">
                          {event.actual && (
                            <div>
                              <div className="text-gray-500">Actual</div>
                              <div className="font-medium text-green-400">{event.actual}</div>
                            </div>
                          )}
                          {event.estimate && (
                            <div>
                              <div className="text-gray-500">Estimate</div>
                              <div className="font-medium text-gray-300">{event.estimate}</div>
                            </div>
                          )}
                          {event.previous && (
                            <div>
                              <div className="text-gray-500">Previous</div>
                              <div className="font-medium text-gray-400">{event.previous}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Event detail overlay */}
    <EventDetailOverlay event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </>
  );
}
